import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, FlaskConical, Truck, Microscope, Headphones, Sparkles, Star } from "lucide-react";
import ProductCard from "../components/ProductCard";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TESTIMONIALS = [
    {
        name: "M.C.",
        rating: 5,
        quote: "Arrived ahead of schedule and exactly what I was looking for. Nearly gave up on peptides.",
        image: "/products/review-mc.jpg",
    },
    {
        name: "E.R.",
        rating: 5,
        quote: "Very happy with the speed of shipping, Tom was very easy to speak to on emails.",
        image: "/products/review-er.jpg",
    },
    {
        name: "J.W.",
        rating: 4,
        quote: "Good and fast.",
        image: "/products/review-jw.jpg",
    },
    {
        name: "P.P.",
        rating: 5,
        quote: "Easy to order, discreet packaging with lots of protection. The bestest.",
        image: "/products/review-pp.jpg",
    },
];

const FEATURES = [
    { icon: FlaskConical, title: "Lab-Tested Purity", desc: "Every batch independently verified ≥99% by third-party HPLC analysis." },
    { icon: Truck, title: "Fast & Discreet Shipping", desc: "Cold-chain packaging, tracked dispatch, ship globally within 48h." },
    { icon: Microscope, title: "Researcher-Focused", desc: "Built by researchers for researchers. We use what we sell." },
    { icon: Headphones, title: "Excellent Support", desc: "Real humans with scientific backgrounds answering your questions." },
];

const FAQS = [
    {
        q: "How are your peptide sequences bio-optimised for dermal delivery?",
        a: "Our research peptides are bio-optimised through advanced sequence design and high-purity synthesis. Each sequence is engineered for stability and bio-availability under the conditions typical of dermal-delivery research models, with documented purity and identity confirmation provided in every Certificate of Analysis.",
    },
    {
        q: "Do you provide a Certificate of Analysis (CoA)?",
        a: "Yes. Every order includes a downloadable Certificate of Analysis from an independent lab — covering identity (HPLC-MS), purity (HPLC), and microbial testing where applicable.",
    },
    {
        q: "Are these products for human consumption?",
        a: "No. All products on PepStep are sold strictly for in-vitro and laboratory research purposes. They are not intended for human or animal consumption, diagnostic, or therapeutic use.",
    },
    {
        q: "How is the cold chain maintained during shipping?",
        a: "Sensitive peptides ship with insulated packaging. We use tracked express couriers and target sub-72-hour delivery for international orders.",
    },
    {
        q: "What is your reshipment / replacement policy?",
        a: "If a shipment is lost in transit or arrives compromised, contact us within 7 days with photos and we'll reship at no charge.",
    },
];

export default function Home() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios.get(`${API}/products`).then((r) => {
            setProducts(r.data);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="hero-glow relative overflow-hidden pt-32 md:pt-40 pb-20 md:pb-28">
                <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
                    <div className="grid md:grid-cols-12 gap-12 items-center">
                        <div className="md:col-span-7">
                            <div className="inline-flex items-center gap-2 bg-white border border-pep-border/60 rounded-full px-4 py-1.5 text-xs text-pep-navy/80 mb-6">
                                <Sparkles className="w-3 h-3 text-pep-primary" />
                                Lab-verified · ≥99% purity · Australian owned and operated
                            </div>
                            <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-[84px] leading-[0.95] tracking-tight text-pep-navy font-medium" data-testid="hero-heading">
                                Premium Peptides
                                <br />
                                for{" "}
                                <span className="inline-block pb-2 pr-1 bg-gradient-to-r from-pep-primary to-pep-accent bg-clip-text text-transparent pulse-aura">
                                    Serious Researchers
                                </span>
                            </h1>
                            <p className="mt-6 text-lg text-pep-muted max-w-xl leading-relaxed">
                                High-purity, lab-tested research peptides delivered with trust and transparency Australia.
                                Built by researchers for researchers, because your work deserves the raw materials necessary
                                for real results.
                            </p>
                            <div className="mt-10 flex flex-wrap gap-4">
                                <Link to="/all" className="btn-pep-primary" data-testid="hero-browse-cta">
                                    Browse Products <ArrowRight className="w-4 h-4" />
                                </Link>
                                <Link to="/about" className="btn-pep-secondary" data-testid="hero-learn-cta">
                                    Learn More
                                </Link>
                            </div>
                            <div className="mt-12 flex items-center gap-8 text-sm text-pep-muted">
                                <div className="flex items-center gap-2">
                                    <div className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-pep-primary/10 text-pep-primary">
                                        <ShieldCheck className="w-4 h-4" />
                                    </div>
                                    <span>300+ researchers</span>
                                </div>
                                <div className="hidden sm:flex items-center gap-1">
                                    {[...Array(5)].map((_, i) => (
                                        <Star key={i} className="w-4 h-4 fill-pep-primary text-pep-primary" />
                                    ))}
                                    <span className="ml-2">4.8 / 5</span>
                                </div>
                            </div>
                        </div>
                        <div className="md:col-span-5 relative">
                            <div className="relative aspect-square rounded-[2.5rem] overflow-hidden border border-pep-border/50 lift">
                                <img
                                    src="https://static.prod-images.emergentagent.com/jobs/4fb1ab17-a32e-48d0-81f2-cca24b2f96aa/images/53afefe31c1fdf3bf32756cb557fc47f32bbff4b5e77c2fab3ce3c7c53f9e9f6.png"
                                    alt="Molecular structure"
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute bottom-4 left-4 right-4 glass-card rounded-2xl p-4 flex items-center justify-between">
                                    <div>
                                        <p className="text-[10px] uppercase tracking-widest text-pep-muted">Latest batch</p>
                                        <p className="font-heading text-pep-navy font-medium">PS-2026-04 · 99.7% purity</p>
                                    </div>
                                    <span className="text-[10px] bg-pep-primary text-white px-2 py-1 rounded-full">Verified</span>
                                </div>
                            </div>
                            <div className="absolute -top-6 -right-6 w-32 h-32 bg-pep-primary/10 rounded-full blur-3xl animate-float" />
                        </div>
                    </div>
                </div>
            </section>

            {/* TRUST MARQUEE */}
            <section className="py-10 border-y border-pep-border/50 overflow-hidden">
                <div className="flex gap-12 marquee whitespace-nowrap text-pep-muted text-sm uppercase tracking-widest">
                    {Array.from({ length: 2 }).map((_, i) => (
                        <React.Fragment key={i}>
                            <span>Third-Party HPLC Tested</span>
                            <span>·</span>
                            <span>Cold-Chain Shipping</span>
                            <span>·</span>
                            <span>Discreet Packaging</span>
                            <span>·</span>
                            <span>Certificate of Analysis Included</span>
                            <span>·</span>
                            <span>Global Dispatch</span>
                            <span>·</span>
                        </React.Fragment>
                    ))}
                </div>
            </section>

            {/* FEATURED PRODUCTS */}
            <section className="py-24 md:py-32" data-testid="home-featured-products">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
                        <div>
                            <p className="text-pep-primary text-sm uppercase tracking-widest font-medium mb-3">Catalog</p>
                            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight text-pep-navy font-medium max-w-2xl">
                                Premium peptides, ready to research.
                            </h2>
                        </div>
                        <Link to="/all" className="text-pep-primary font-medium flex items-center gap-2 hover:gap-3 transition-all">
                            View all <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {loading
                            ? Array.from({ length: 6 }).map((_, i) => (
                                  <div key={i} className="rounded-3xl bg-pep-surface aspect-[3/4] animate-pulse" />
                              ))
                            : products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="py-24 md:py-32 bg-pep-surface" data-testid="home-testimonials">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="text-center mb-16">
                        <p className="text-pep-primary text-sm uppercase tracking-widest font-medium mb-3">Testimonials</p>
                        <h2 className="font-heading text-4xl sm:text-5xl tracking-tight text-pep-navy font-medium">
                            What our customers say.
                        </h2>
                        <p className="text-pep-muted mt-4 text-lg">Trusted by researchers across the globe.</p>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                        {TESTIMONIALS.map((t, i) => (
                            <div
                                key={i}
                                data-testid={`testimonial-${i}`}
                                className="bg-white rounded-3xl p-8 border border-pep-border/50 lift overflow-hidden"
                            >
                                {t.image && (
                                    <div className="rounded-2xl overflow-hidden mb-6 aspect-[4/3] bg-pep-surface">
                                        <img
                                            src={t.image}
                                            alt={`${t.name} review`}
                                            className="w-full h-full object-contain"
                                            loading="lazy"
                                        />
                                    </div>
                                )}
                                <div className="flex gap-1 mb-4">
                                    {[...Array(5)].map((_, j) => (
                                        <Star
                                            key={j}
                                            className={`w-4 h-4 ${
                                                j < t.rating
                                                    ? "fill-pep-primary text-pep-primary"
                                                    : "fill-pep-border text-pep-border"
                                            }`}
                                        />
                                    ))}
                                </div>
                                <p className="text-pep-navy text-lg leading-relaxed mb-6">"{t.quote}"</p>
                                <p className="font-medium text-pep-navy tracking-wider">{t.name}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY CHOOSE US */}
            <section className="py-24 md:py-32" data-testid="home-why-choose-us">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="grid md:grid-cols-12 gap-12">
                        <div className="md:col-span-4">
                            <p className="text-pep-primary text-sm uppercase tracking-widest font-medium mb-3">Why PepStep</p>
                            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight text-pep-navy font-medium leading-tight">
                                The standard you've been looking for.
                            </h2>
                        </div>
                        <div className="md:col-span-8 grid sm:grid-cols-2 gap-4">
                            {FEATURES.map((f, i) => (
                                <div key={i} data-testid={`feature-${i}`} className="p-6 rounded-3xl border border-pep-border/60 bg-white lift">
                                    <div className="w-12 h-12 rounded-2xl bg-pep-primary/10 text-pep-primary flex items-center justify-center mb-4">
                                        <f.icon className="w-5 h-5" />
                                    </div>
                                    <h3 className="font-heading text-lg font-medium text-pep-navy mb-2">{f.title}</h3>
                                    <p className="text-pep-muted text-sm leading-relaxed">{f.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-24 md:py-32 bg-pep-surface" data-testid="home-faq">
                <div className="max-w-4xl mx-auto px-6 md:px-12">
                    <div className="text-center mb-16">
                        <p className="text-pep-primary text-sm uppercase tracking-widest font-medium mb-3">FAQ</p>
                        <h2 className="font-heading text-4xl sm:text-5xl tracking-tight text-pep-navy font-medium">
                            Questions, answered.
                        </h2>
                    </div>
                    <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
                        {FAQS.map((f, i) => (
                            <AccordionItem
                                key={i}
                                value={`item-${i}`}
                                className="bg-white rounded-2xl px-6 border border-pep-border/50"
                                data-testid={`faq-item-${i}`}
                            >
                                <AccordionTrigger className="text-left font-heading text-lg font-medium text-pep-navy hover:no-underline py-5">
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-pep-muted leading-relaxed pb-6">
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>
        </div>
    );
}
